import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
    Box,
    Button,
    Chip,
    MenuItem,
    Paper,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import axios from 'axios';

import { RootState } from '../redux/store';
import { BFF_BASE_URL } from '../auth/authApi';
import { LoadingButton } from '../shared/components/LoadingButton';
import { Section } from '../shared/components/Section';
import { GlowAlert } from '../shared/components/GlowAlert';


interface MessageWithFeedback {
    id: string;
    conversation_id: string;
    role: 'user' | 'assistant';
    content: string;
    timestamp: string;
    channel?: string | null;
    status?: string | null;
    feedback_type?: 'up' | 'down' | null;
    feedback_text?: string | null;
}

interface FeedbackData {
    items: MessageWithFeedback[];
    next_cursor: string | null;
}

interface ReportingPageProps {
    userId?: string;
    onSelectUser: (userId: string) => void;
}

/** The messages one user rated, newest first. Lives in the Rated messages tab of the Quality page. */
const ReportingPage: React.FC<ReportingPageProps> = ({ userId, onSelectUser }) => {
    const users = useSelector((state: RootState) => state.users.users);
    const [messages, setMessages] = useState<MessageWithFeedback[]>([]);
    const [nextCursor, setNextCursor] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [feedbackFilter, setFeedbackFilter] = useState<'all' | 'up' | 'down'>('all');

    const load = useCallback(
        async (cursor?: string) => {
            if (!userId) return;
            setLoading(true);
            setError(null);
            try {
                const response = await axios.get<FeedbackData>(
                    `${BFF_BASE_URL}/admin/users/${encodeURIComponent(userId)}/feedback`,
                    {
                        params: {
                            cursor,
                            limit: 50,
                            feedback: feedbackFilter === 'all' ? undefined : feedbackFilter,
                        },
                    }
                );
                setMessages((prev) => (cursor ? [...prev, ...response.data.items] : response.data.items));
                setNextCursor(response.data.next_cursor);
            } catch (err) {
                setError(
                    axios.isAxiosError(err)
                        ? err.response?.data?.error || err.message
                        : 'Failed to load feedback'
                );
            } finally {
                setLoading(false);
            }
        },
        [userId, feedbackFilter]
    );

    useEffect(() => {
        setMessages([]);
        setNextCursor(null);
        load();
    }, [load]);

    // The BFF only returns messages with feedback and applies the filter itself.
    const filteredMessages = messages;

    return (
        <Section title="Feedback & Reporting" onRefresh={() => load()} refreshing={loading}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                <TextField
                    select
                    size="small"
                    label="User"
                    value={users.some((u) => u.id === userId) ? userId : ''}
                    onChange={(e) => onSelectUser(e.target.value)}
                    sx={{ minWidth: 280 }}
                >
                    {users.map((u) => (
                        <MenuItem key={u.id} value={u.id}>
                            {u.display_name || u.email || u.id}
                            {u.message_count !== undefined ? ` (${u.message_count})` : ''}
                        </MenuItem>
                    ))}
                </TextField>
                <TextField
                    select
                    size="small"
                    label="Feedback"
                    value={feedbackFilter}
                    onChange={(e) => setFeedbackFilter(e.target.value as any)}
                    sx={{ minWidth: 160 }}
                >
                    <MenuItem value="all">All Messages</MenuItem>
                    <MenuItem value="up">Thumbs Up Only</MenuItem>
                    <MenuItem value="down">Thumbs Down Only</MenuItem>
                </TextField>
            </Box>

            {!userId && <Typography color="text.secondary">Select a user to see their feedback.</Typography>}

            {error && <GlowAlert sx={{ mb: 2 }}>{error}</GlowAlert>}

            <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                    {filteredMessages.length} messages {feedbackFilter !== 'all' ? `with thumbs ${feedbackFilter}` : ''}
                </Typography>
            </Box>

            {filteredMessages.map((m) => (
                <Paper
                    key={m.id}
                    variant="outlined"
                    sx={{
                        p: 1.5,
                        mb: 1,
                        backgroundColor: m.feedback_type === 'down' ? 'rgba(211, 47, 47, 0.05)' : 'background.paper',
                        borderLeft: 4,
                        borderLeftColor: m.role === 'user' ? 'primary.main' : 'secondary.main',
                    }}
                >
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 0.5, flexWrap: 'wrap' }}>
                        <Typography variant="subtitle2">{m.role === 'user' ? 'User' : 'VictorIA'}</Typography>
                        {m.channel && <Chip size="small" label={m.channel} />}
                        {m.status === 'failed' && <Chip size="small" color="error" label="failed" />}
                        {m.feedback_type === 'up' && (
                            <Tooltip title="User gave thumbs up">
                                <ThumbUpIcon sx={{ fontSize: 16, color: 'success.main' }} />
                            </Tooltip>
                        )}
                        {m.feedback_type === 'down' && (
                            <Tooltip
                                title={m.feedback_text || 'User gave thumbs down'}
                                arrow
                                sx={{
                                    '& .MuiTooltip-tooltip': {
                                        maxWidth: 400,
                                        textAlign: 'left',
                                    },
                                }}
                            >
                                <ThumbDownIcon sx={{ fontSize: 16, color: 'error.main' }} />
                            </Tooltip>
                        )}
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                            {new Date(m.timestamp).toLocaleString()}
                        </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {m.content.substring(0, 300)}
                        {m.content.length > 300 && '...'}
                    </Typography>
                    {m.feedback_text && (
                        <Box sx={{ mt: 1, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                                Feedback:
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                                "{m.feedback_text}"
                            </Typography>
                        </Box>
                    )}
                </Paper>
            ))}

            {!loading && !error && filteredMessages.length === 0 && (
                <Typography color="text.secondary">No feedback yet.</Typography>
            )}
            {loading && messages.length === 0 && <GlowAlert severity="info">Loading feedback...</GlowAlert>}
            {nextCursor && (
                <LoadingButton variant="outlined" loading={loading} onClick={() => load(nextCursor)}>
                    Load more
                </LoadingButton>
            )}
        </Section>
    );
};

export default ReportingPage;
